import { useEffect, useState } from "react";
import { Bell, ArrowDown } from "lucide-react";
import PopupModal from "../utilPages/PopupModal";
import { hasActiveOnlineClubPass, hasActiveStudioPass } from "../../utils/communityPassHelpers";

function OnlineToggle({ value, onChange, disabled, labelOnline, labelStudio, loadingLabel, isLoading }) {
  // Only show loading text while request in flight; disabled+locked should still show normal labels
  const showLoading = disabled && isLoading && loadingLabel;
  return (
    <div className="mt-3 flex items-center gap-2 text-sm select-none justify-center">
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        disabled={disabled}
        onClick={() => !disabled && onChange(!value)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          value ? "bg-pink" : "bg-gray-300"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            value ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
      <button
        type="button"
        className={`text-sm ${value ? "text-black font-medium" : "text-gray-400"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${!disabled && !value ? "hover:text-gray-500" : ""}`}
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
      >
        {showLoading ? loadingLabel : (value ? labelOnline : labelStudio)}
      </button>
    </div>
  );
}

export default function ClassModal({
  showModal,
  selectedClass,
  user,
  token,
  language,
  details,
  getBookingForClass,
  isClassDateWithinAnyPass,
  getActivePassForUserAndLocation,
  isPassExhausted,
  translateDayName,
  translateMonth,
  translateLocationName,
  handleConfirmBooking,
  handleCancelBooking,
  handleToggleBookingOnline,
  togglingOnlineClassId,
  cancelingFromModal,
  setCancelingFromModal,
  bookingStatus,
  setShowModal,
  setSelectedClass,
  setShowPurchaseModal,
  getActivePassForUser,
  loginWithRedirect,
  isAdmin,
  handleJoinWaitlist,
  handleLeaveWaitlist,
  setToaster,
}) {
  const [joinOnline, setJoinOnline] = useState(false);

  /** Class is online-only; join-online toggle is forced on and disabled. */
  const isOnlineOnlyClass = selectedClass?.type === "online";
  /** Whether this class can be joined online at all (mirrors server isPassValidForLocation). */
  const classAllowsJoinOnline = isOnlineOnlyClass || selectedClass?.allowJoinOnline === true;
  /** Online-club-only users must book as online; cannot toggle to studio. */
  const onlineClubOnly =
    !!user &&
    hasActiveOnlineClubPass(user) &&
    !hasActiveStudioPass(user);
  /** In-person class with no studio spots left (server: bookedCount vs capacity). Online booking stays open. */
  const studioCapacityFull = (() => {
    if (!selectedClass || isOnlineOnlyClass || selectedClass.capacity == null) return false;
    if ((selectedClass.bookedCount ?? 0) >= selectedClass.capacity) return true;
    // Fallback: count confirmed in-studio bookings from the bookings array in case bookedCount is stale
    const confirmedInStudio = (selectedClass.bookings || []).filter(
      (b) => b.status === "confirmed" && !b.online
    ).length;
    return confirmedInStudio >= selectedClass.capacity;
  })();
  const joinOnlineLocked = onlineClubOnly;
  /** Hide physical location; prefer online class link (same for role or pass-only online club, or purely-online classes). */
  const isNonOnlineClubUser = !!user && !onlineClubOnly;
  const hidePhysicalLocation =
    isOnlineOnlyClass || onlineClubOnly || (studioCapacityFull && isNonOnlineClubUser);

  const getBookingCounts = (classObj) => {
    const bookings = classObj?.bookings || [];
    const confirmed = bookings.filter((b) => b.status === "confirmed");
    const online = confirmed.filter((b) => b.online === true).length;
    const offline = confirmed.filter((b) => !b.online).length;
    return { online, offline };
  };

  useEffect(() => {
    const pendingJoinOnline = window.localStorage.getItem("pendingJoinOnline");
    if (pendingJoinOnline === "true" || pendingJoinOnline === "false") {
      setJoinOnline(pendingJoinOnline === "true");
      window.localStorage.removeItem("pendingJoinOnline");
      return;
    }

    // Online-only class: no studio option; always book as online (toggle hidden).
    if (selectedClass?.type === "online") {
      setJoinOnline(true);
      return;
    }

    // Online-club-only pass: always default to join online (cannot book as studio).
    if (user && hasActiveOnlineClubPass(user) && !hasActiveStudioPass(user)) {
      setJoinOnline(true);
      return;
    }

    const cap = selectedClass?.capacity;
    const fullByCount =
      selectedClass &&
      selectedClass.type !== "online" &&
      cap != null &&
      (selectedClass.bookedCount ?? 0) >= cap;
    const fullByBookings =
      selectedClass &&
      selectedClass.type !== "online" &&
      cap != null &&
      (selectedClass.bookings || []).filter(
        (b) => b.status === "confirmed" && !b.online
      ).length >= cap;
    if (fullByCount || fullByBookings) {
      setJoinOnline(false);
      return;
    }

    setJoinOnline(false);
  }, [
    selectedClass?._id,
    selectedClass?.type,
    selectedClass?.capacity,
    selectedClass?.bookedCount,
    showModal,
    user,
  ]);

  // Keep joinOnline true while locked or for online-only class so confirm always sends online.
  useEffect(() => {
    const mustBeOnline =
      joinOnlineLocked || selectedClass?.type === "online";
    if (mustBeOnline && !joinOnline) setJoinOnline(true);
  }, [joinOnlineLocked, joinOnline, selectedClass?.type]);

  if (!showModal || !selectedClass) return null;

  const booking = getBookingForClass(selectedClass._id);
  const now = new Date();
  const eightHoursFromNow = new Date(now.getTime() + 8 * 60 * 60 * 1000);
  const classTime = new Date(selectedClass.start);
  const isWithin8Hours = classTime <= eightHoursFromNow;
  const bookAsOnline =
    joinOnlineLocked || joinOnline || isOnlineOnlyClass;
  /** Video link vs address mirrors the current online selection: booked → booking.online;
   * not yet booked → the join-online toggle (bookAsOnline), or a forced-online case. */
  const showVideoLink = booking
    ? isOnlineOnlyClass || !!booking.online
    : hidePhysicalLocation || bookAsOnline;

  const isOnWaitlist = user && selectedClass.waitingList?.some(
    (entry) => {
      const entryId = entry.userId?._id || entry.userId;
      return entryId && String(entryId) === String(user._id);
    }
  );

  return (
    <PopupModal
      onClose={() => {
        setShowModal(false);
        setSelectedClass(null);
        setCancelingFromModal(false);
        setJoinOnline(false);
      }}
    >
      <div className="p-4">
        <div className="mb-4 text-center">
          <h2 className="text-xl font-bold text-center">
            {selectedClass.title || selectedClass.description}
          </h2>
          {selectedClass.title && selectedClass.description && (
            <p className="text-sm text-gray-500 mt-1">{selectedClass.description}</p>
          )}
        </div>
        <div className="text-gray-600 mb-4">
          <span>
            {(() => {
              const d = new Date(selectedClass.start);
              const timeStr = d.toLocaleTimeString("en-GB", {
                timeZone: "Asia/Jerusalem",
                hour: "2-digit",
                minute: "2-digit",
              });
              const dayStr = d.toLocaleDateString("en-GB", {
                timeZone: "Asia/Jerusalem",
                day: "numeric",
              });
              const monthAbbr = d.toLocaleDateString("en-GB", { timeZone: "Asia/Jerusalem", month: "short" });
              const durationPart =
                selectedClass.durationMinutes != null
                  ? ` · ${selectedClass.durationMinutes} ${details.min}`
                  : "";
              return `${timeStr}, ${dayStr} ${translateMonth(monthAbbr)}${durationPart}`;
            })()}
          </span>
        </div>

        {isAdmin && (() => {
          const { online, offline } = getBookingCounts(selectedClass);
          if (online === 0 && offline === 0) return null;
          return (
            <div className="mb-4 flex items-center gap-3 text-sm mx-auto w-fit">
              {online > 0 && <span className="text-blue-600 font-medium">🖥️ {online} {language === "russian" ? "онлайн" : "online"}</span>}
              {offline > 0 && <span className="text-green-700 font-medium">🏠 {offline} {language === "russian" ? "вживую" : "in person"}</span>}
            </div>
          );
        })()}

        {selectedClass.videoCallUrl && showVideoLink ? (
          <div className="text-gray-600 mb-4">
            <a
              href={selectedClass.videoCallUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brown hover:underline font-semibold"
            >
              🔗 {details.videoCallLink}
            </a>
          </div>
        ) : !showVideoLink && selectedClass.locationName ? (
          <div className="text-gray-600 mb-4">
            <a
              href={selectedClass.locationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brown hover:underline font-semibold"
            >
              📍{" "}
              {translateLocationName(
                selectedClass.locationName
              )}
            </a>
          </div>
        ) : null}

        {booking ? (
          // User has already booked this class
          <>
            <div className={`mb-4 p-2 rounded-lg w-fit mx-auto ${booking.online ? "bg-blue-100" : "bg-green-100"}`}>
              <span className={`font-semibold px-4 ${booking.online ? "text-blue-700" : "text-green-700"}`}>
                {booking.online ? details.bookedOnline : details.booked}
              </span>
            </div>

            {/* Toggle Join online when booked — forced on & disabled for online-only classes; hidden entirely when the class doesn't allow joining online */}
            {handleToggleBookingOnline && classAllowsJoinOnline && (
              <div className="mb-4">
                <OnlineToggle
                  value={isOnlineOnlyClass ? true : !!booking.online}
                  onChange={(v) => {
                    // Online-club-only: cannot switch from online back to studio.
                    if (joinOnlineLocked && booking.online && !v) return;
                    handleToggleBookingOnline(selectedClass._id, v);
                  }}
                  disabled={
                    isOnlineOnlyClass ||
                    togglingOnlineClassId === selectedClass._id ||
                    (joinOnlineLocked && !!booking.online)
                  }
                  isLoading={togglingOnlineClassId === selectedClass._id}
                  labelOnline={details.joinOnline}
                  labelStudio={details.joinOnline}
                  loadingLabel={details.loadingDotDotDot}
                />
              </div>
            )}

            {cancelingFromModal ? (
              <div className="flex flex-col gap-2 w-fit mx-auto">
                <button
                  className={`border border-red-200 text-red-500 font-semibold bg-red-50
                  rounded-lg px-4 py-2 w-fit mx-auto
                  transition
                  hover:bg-red-100 hover:text-red-600 ${
                    isWithin8Hours ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={() => {
                    if (isWithin8Hours) {
                      setToaster?.({ message: details.cannotCancelWithin12Hours || "Bookings cannot be cancelled less than 8 hours before class", type: "warning", show: true });
                      return;
                    }
                    handleCancelBooking(selectedClass._id);
                  }}
                  type="button"
                >
                  {details.confirmCancel}
                </button>
                <button
                  className="border-2 font-semibold border-gray-200 text-gray-500 bg-gray-50 rounded-lg px-4 py-2 w-fit mx-auto transition hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => setCancelingFromModal(false)}
                  type="button"
                >
                  {details.keepBooking}
                </button>
              </div>
            ) : (
              <button
                className={`
                  border border-red-200 text-red-500 bg-red-50
                  rounded-lg px-2 py-1 font-normal w-fit mx-auto
                  transition
                  hover:bg-red-100 hover:text-red-600
                  ${isWithin8Hours ? "opacity-50 cursor-not-allowed" : ""}
                `}
                onClick={() => {
                  if (isWithin8Hours) {
                    setToaster?.({ message: details.cannotCancelWithin12Hours || "Bookings cannot be cancelled less than 8 hours before class", type: "warning", show: true });
                    return;
                  }
                  setCancelingFromModal(true);
                }}
                type="button"
              >
                {details.cancelBooking}
              </button>
            )}
          </>
        ) : (
          // User hasn't booked yet
          <>
            {/* Book Button (same as before) + small join-online toggle */}
            {(() => {
              const bookingOnline =
                selectedClass?.type === "online" ? true : joinOnline;
              const activePass = getActivePassForUserAndLocation(
                selectedClass,
                bookingOnline
              );
              const canBookClass =
                token &&
                activePass &&
                !isPassExhausted(activePass) &&
                isClassDateWithinAnyPass(selectedClass.start);

              // Subscription pass can't cover this class date → disable purchase until renewal (any role)
              const classDateTime = new Date(selectedClass.start);
              const hasActiveSubscriptionPass = user?.passes?.some(
                (pass) =>
                  pass.isSubscription === true &&
                  pass.status === "active" &&
                  new Date(pass.expiresAt) > new Date() &&
                  (!pass.classesIncluded ||
                    pass.classesUsed < pass.classesIncluded)
              );
              // Class is after this subscription pass's period end (expiresAt) — can't book until renew extends period
              const subscriptionPassExistsButDatePastPeriod = user?.passes?.some(
                (pass) =>
                  pass.isSubscription === true &&
                  pass.status === "active" &&
                  new Date(pass.expiresAt) > new Date() &&
                  classDateTime > new Date(pass.expiresAt)
              );
              const dateNotCovered = !isClassDateWithinAnyPass(selectedClass.start);
              const disablePurchaseForSubscriptionDate =
                !!user &&
                !canBookClass &&
                hasActiveSubscriptionPass &&
                (dateNotCovered || subscriptionPassExistsButDatePastPeriod);
              const purchaseDisabled = disablePurchaseForSubscriptionDate;
              const hideSignUpButton = !!user && studioCapacityFull && !joinOnline && !onlineClubOnly;

              return (
                <div className="flex flex-col items-center">
                  <div className="min-h-[4.5rem] flex items-center justify-center mt-4">
                    {hideSignUpButton ? (
                      <div className="flex flex-col items-center mx-auto max-w-[16rem]">
                        <p className="text-sm text-gray-500 text-center">{details.studioFullJoinOnline}</p>
                        <ArrowDown size={14} className="text-gray-400 mt-1 animate-bounce" />
                      </div>
                    ) : (
                      <button
                        className={`rounded-xl px-5 py-2 transition w-fit mx-auto ${
                          purchaseDisabled
                            ? "bg-brown/50 text-white/90 cursor-not-allowed opacity-70"
                            : "bg-brown text-white hover:bg-beige hover:text-brown"
                        }`}
                      onClick={() => {
                        if (!user) {
                          sessionStorage.setItem(
                            "scrollPosition",
                            window.scrollY.toString()
                          );
                          window.localStorage.setItem(
                            "pendingRedirectTo",
                            "/schedule"
                          );
                          window.localStorage.setItem("redirectTo", "/schedule");
                          window.localStorage.setItem(
                            "pendingClassId",
                            selectedClass._id
                          );
                          window.localStorage.setItem(
                            "openBookingModal",
                            "true"
                          );
                          window.localStorage.setItem(
                            "pendingJoinOnline",
                            bookAsOnline ? "true" : "false"
                          );
                          loginWithRedirect();
                          return;
                        }
                        handleConfirmBooking(bookAsOnline);
                      }}
                      disabled={bookingStatus === "loading" || purchaseDisabled}
                      type="button"
                    >
                      {!user
                        ? details.loginToBook
                        : bookingStatus === "loading"
                        ? details.booking
                        : canBookClass || !activePass
                        ? details.confirmBooking
                        : details.buyNow}
                      </button>
                    )}
                  </div>

                  {(() => {
                    const showToggle =
                      classAllowsJoinOnline &&
                      (isOnlineOnlyClass || !user || canBookClass || studioCapacityFull);
                    if (!showToggle) return null;
                    return (
                      <OnlineToggle
                        value={isOnlineOnlyClass || joinOnlineLocked ? true : joinOnline}
                        onChange={setJoinOnline}
                        disabled={isOnlineOnlyClass || bookingStatus === "loading" || joinOnlineLocked}
                        labelOnline={details.joinOnline}
                        labelStudio={details.joinOnline}
                      />
                    );
                  })()}
                  {studioCapacityFull && user && token && !isOnlineOnlyClass && !onlineClubOnly && handleJoinWaitlist && (
                    <div className="mt-3 flex flex-col items-center">
                      {isOnWaitlist ? (
                        <>
                          <p className="text-xs text-green-700 font-medium">{details.onWaitingList}</p>
                          <button
                            type="button"
                            className="text-[11px] text-gray-400 hover:text-red-400 mt-0.5 transition"
                            onClick={() => handleLeaveWaitlist(selectedClass._id)}
                          >
                            {details.leaveWaitingList}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-xs text-brown underline hover:text-brown/80 font-medium transition"
                          onClick={() => handleJoinWaitlist(selectedClass._id)}
                        >
                          <Bell size={13} />
                          {details.notifyMe}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>
    </PopupModal>
  );
}